import * as React from "react";
import { useLayoutEffect, useEffect, useRef, useCallback } from "react";

/**
 * A textarea whose height follows its content.
 *
 * Phones (below md): compact. The floor is one line, so an empty box is only
 * as tall as its placeholder (Chromium and WebKit both count the placeholder
 * in scrollHeight), and growth stops at half the viewport, after which the
 * box scrolls internally. The `!` is deliberate: call sites pass desktop
 * floors like min-h-[180px], and a writer on a phone should not scroll past
 * empty space to reach the next field.
 *
 * Desktop is unchanged: 80px floor, no cap.
 */
const AutoExpandTextarea = React.forwardRef(({ className, value, onChange, onInput, ...props }, ref) => {
  const internalRef = useRef(null);

  const resize = useCallback(() => {
    const el = internalRef.current;
    if (!el) return;
    // Reset to auto so shrinking works, then lock to the content height
    el.style.height = 'auto';
    const border = el.offsetHeight - el.clientHeight;
    const wanted = el.scrollHeight + border;
    const max = parseFloat(getComputedStyle(el).maxHeight); // NaN when "none"
    const capped = Number.isFinite(max) && wanted > max;
    el.style.height = `${capped ? max : wanted}px`;
    el.style.overflowY = capped ? 'auto' : 'hidden';
  }, []);

  // Run synchronously before paint whenever value changes (handles API-loaded data)
  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  // A height measured once goes stale: the box is first measured before the
  // layout settles and before Courier Prime loads, so text wraps differently
  // afterwards. Measured on a 375px phone, empty boxes sat at 136–208px against
  // a natural 78px. Re-measure when the WIDTH changes (rotation, sidebar,
  // late layout) and when web fonts finish loading. Height changes are
  // ignored — they are our own writes — EXCEPT when CSS has clamped the box
  // below its content while it is not scrolling: the 50vh cap follows the
  // viewport height, so a shorter viewport (split screen, a resized window)
  // would otherwise leave the end of the text clipped and unreachable.
  // Deferred a frame so resizing inside the observer callback does not
  // trip "ResizeObserver loop completed with undelivered notifications".
  useEffect(() => {
    const el = internalRef.current;
    if (!el) return undefined;
    let width = el.clientWidth;
    let ro;
    const nextFrame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 0);
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        const clipped = el.style.overflowY !== 'auto' && el.scrollHeight > el.clientHeight;
        if (el.clientWidth !== width || clipped) {
          width = el.clientWidth;
          nextFrame(resize);
        }
      });
      ro.observe(el);
    }
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    let alive = true;
    fonts?.ready?.then(() => { if (alive) resize(); });
    fonts?.addEventListener?.('loadingdone', resize);
    return () => {
      alive = false;
      ro?.disconnect();
      fonts?.removeEventListener?.('loadingdone', resize);
    };
  }, [resize]);

  // Also resize on every input event — catches typing, paste, cut, speech input
  const handleInput = (e) => {
    resize();
    if (onInput) onInput(e);
  };

  // Merge the forwarded ref with our internal ref
  const mergedRef = (el) => {
    internalRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  return (
    <textarea
      ref={mergedRef}
      rows={1}
      className={`flex w-full min-h-[80px] max-md:!min-h-[44px] max-md:max-h-[50vh] border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none overflow-hidden ${className || ''}`}
      value={value}
      onChange={onChange}
      onInput={handleInput}
      {...props}
    />
  );
});

AutoExpandTextarea.displayName = "AutoExpandTextarea";

export { AutoExpandTextarea };
